import { PaymentDirection, PaymentMethodPreset, PaymentStatus, } from '@cpce/database/enums';
import { Decimal } from 'decimal.js';
import z from 'zod';
import { zDateField } from '../utils/date.utils.js';
import { zodMoney } from './decimal.types.js';
export const PaymentDirectionSchema = z.enum(PaymentDirection);
export const PaymentStatusSchema = z.enum(PaymentStatus);
export const PAYMENT_STATUS_LABELS = {
    [PaymentStatus.expected]: 'À encaisser',
    [PaymentStatus.notDeposited]: 'Non remis',
    [PaymentStatus.deposited]: 'Remis',
    [PaymentStatus.rejected]: 'Rejeté',
    [PaymentStatus.cancelled]: 'Annulé',
};
export const PAYMENT_DIRECTION_LABELS = {
    [PaymentDirection.inflow]: 'Recette',
    [PaymentDirection.outflow]: 'Dépense',
};
// How far a règlement is from the bank. Derived from its payments, never stored: an échéancier
// banks its cheques one at a time, so it spends most of its life half-way there.
export const REGLEMENT_DEPOSIT_STATUSES = [
    'toDeposit',
    'partlyDeposited',
    'deposited',
    'rejected',
    'cancelled',
    'paidOut',
];
export const REGLEMENT_DEPOSIT_STATUS_LABELS = {
    toDeposit: 'À remettre',
    partlyDeposited: 'Partiellement remis',
    deposited: 'Remis',
    rejected: 'Rejeté',
    cancelled: 'Annulé',
    paidOut: 'Versé',
};
export const goesToTransferFile = (payment) => payment.paymentMethodPreset
    ? payment.paymentMethodPreset === PaymentMethodPreset.virementAEmettre
    : payment.financialAccount.producesTransferFile;
// `goesToTransferFile` as a Prisma filter.
export const transferFilePaymentWhere = {
    OR: [
        { paymentMethodPreset: PaymentMethodPreset.virementAEmettre },
        { paymentMethodPreset: null, financialAccount: { producesTransferFile: true } },
    ],
};
// An encaissement waits for its remise from the moment it is expected (a rescheduled one is the
// treasury forecast, though only a collected one can join a deposit). On the way out, only a
// virement booked on a « virements à émettre » account does: an issued cheque or card payment
// credits the 512 directly and is done the moment it is posted.
export function awaitsRemise(payment) {
    return payment.direction === PaymentDirection.inflow
        ? payment.status === PaymentStatus.expected || payment.status === PaymentStatus.notDeposited
        : payment.status === PaymentStatus.notDeposited && goesToTransferFile(payment);
}
// `awaitsRemise` as a Prisma filter, minus the cash already in its caisse: that is deposited from
// the Caisse page, whose balance is what actually goes to the bank. A cash règlement still to be
// collected stays, since « Encaisser » lives on this list.
export function pendingPaymentsWhere(direction) {
    return direction === PaymentDirection.inflow
        ? {
            direction,
            status: { in: [PaymentStatus.expected, PaymentStatus.notDeposited] },
            NOT: {
                status: PaymentStatus.notDeposited,
                paymentMethodPreset: PaymentMethodPreset.especes,
            },
        }
        : {
            direction,
            status: PaymentStatus.notDeposited,
            ...transferFilePaymentWhere,
        };
}
// Cash is fungible, so a remise from the caisse names an amount, not règlements. The ones it
// settles are taken oldest first while their running total fits in that amount, and all of them
// when it empties the box: no cash is left for the others to be deposited with.
export function pickCoveredPayments(pending, amount, depositDate, emptiesCaisse) {
    const eligible = pending
        .filter((payment) => new Date(payment.emissionDate) <= depositDate)
        .sort((a, b) => new Date(a.emissionDate).getTime() - new Date(b.emissionDate).getTime() || a.id - b.id);
    if (emptiesCaisse)
        return eligible;
    let runningTotal = new Decimal(0);
    const firstUncovered = eligible.findIndex((payment) => {
        runningTotal = runningTotal.plus(payment.amount);
        return runningTotal.greaterThan(amount);
    });
    return firstUncovered === -1 ? eligible : eligible.slice(0, firstUncovered);
}
// A rejected cheque is not waiting for a remise any more (its replacement is a règlement of its
// own), but it must not read « Remis » either: the money came back. An écriture predating the
// treasury module carries no payment at all: it stays « à remettre » rather than disappearing
// behind the list's default filter. A créance paid out to the salarié never goes to the bank's
// counter unless it leaves by batched virement.
export function deriveReglementDepositStatus(reglement) {
    if (reglement.cancelledAt)
        return 'cancelled';
    const { payments } = reglement;
    if (payments.length > 0 &&
        payments.every((payment) => payment.direction === PaymentDirection.outflow)) {
        return payments.some(awaitsRemise) ? 'toDeposit' : 'paidOut';
    }
    if (payments.every(awaitsRemise))
        return 'toDeposit';
    if (payments.some(awaitsRemise))
        return 'partlyDeposited';
    return payments.some((payment) => payment.status === PaymentStatus.rejected)
        ? 'rejected'
        : 'deposited';
}
export const remiseTypeKey = (paymentMethod) => `${paymentMethod.financialAccountId}:${paymentMethod.paymentMethodPreset ?? ''}`;
// Incoming règlements and outgoing virements wait on two separate screens, but go through the same
// list, remise and history.
export const pendingPaymentsFiltersSchema = z.object({
    direction: PaymentDirectionSchema.default(PaymentDirection.inflow),
});
// Euro notes and coins, largest first — the order a cash deposit slip lists them in, and the order
// anyone counts a till in. 500 is still legal tender even though it is no longer issued, so a CSE
// handed one must be able to declare it.
export const CASH_DENOMINATIONS = [
    '500',
    '200',
    '100',
    '50',
    '20',
    '10',
    '5',
    '2',
    '1',
    '0.50',
    '0.20',
    '0.10',
    '0.05',
    '0.02',
    '0.01',
];
// Denomination → how many of them. Zero-count denominations are dropped rather than stored as 0,
// so the slip only prints the coupures actually handed over. `partialRecord` and not `record`:
// with an enum key the latter demands *every* denomination, so a deposit of one note would be
// rejected for not declaring the fourteen others.
export const cashBreakdownSchema = z.partialRecord(z.enum(CASH_DENOMINATIONS), z.number().int().nonnegative());
export function sumCashBreakdown(breakdown) {
    return Object.entries(breakdown).reduce((total, [denomination, count]) => total.plus(new Decimal(denomination).times(count ?? 0)), new Decimal(0));
}
export const createBankDepositSchema = z.object({
    bankId: z.number().int().positive('Le compte bancaire est requis'),
    bankReference: z.string().optional(),
    // Actual deposit date, and the écriture's date: the date d'exécution was dropped from the flow,
    // and the lag until the real credit is absorbed by the bank reconciliation. Normalized to
    // midnight — an écriture carrying a time of day falls outside an exercice whose `endDate` is a
    // plain calendar day, and drops out of the FEC and the états comptables.
    depositDate: zDateField('La date de remise est requise'),
    paymentIds: z.array(z.number().int().positive()).min(1, 'Au moins un règlement est requis'),
});
// A remise from the caisse: the coupures handed over are the amount, since that is what the teller
// counts the bag against.
export const createCashDepositSchema = z.object({
    bankId: z.number().int().positive('Le compte bancaire est requis'),
    bankReference: z.string().optional(),
    depositDate: zDateField('La date de remise est requise'),
    cashBreakdown: cashBreakdownSchema.refine((breakdown) => sumCashBreakdown(breakdown).gt(0), {
        message: 'Le montant de la remise doit être supérieur à zéro',
    }),
});
// A remise corrected before the bank reconciliation: where the money went (or came from) and when.
export const updateBankDepositSchema = z.object({
    bankId: z.number().int().positive('Le compte bancaire est requis'),
    depositDate: zDateField('La date de remise est requise'),
});
// The number depends on the deposit date's year, so the forecast must follow the modal's field and
// not merely the day it is opened. Remises de virements are numbered in their own series.
export const nextDepositSlipNumberSchema = z.object({
    depositDate: z.coerce.date(),
    direction: PaymentDirectionSchema.default(PaymentDirection.inflow),
});
export const paymentsFiltersSchema = z.object({
    direction: PaymentDirectionSchema.optional(),
    status: PaymentStatusSchema.optional(),
});
// The history grows without bound, unlike the pending list which empties itself, so its period is
// filtered server-side. The client owns the default range (current month) because it also has to
// display it.
export const bankDepositsHistoryFiltersSchema = z.object({
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
});
// The two accounts a rejection posts to besides the 411 and the 512, resolved from the CSE's own
// chart by PCG prefix. Kept here so the message naming a missing one, the resolution and the
// preview all read the same list.
// Cash modes (53x) have no dedicated transit account: the caisse itself plays that role, and its
// deposit is an internal transfer caisse → banque, posted on the bank's own journal.
export const CASH_ACCOUNT_PREFIX = '53';
export const REJECTION_ACCOUNT_PREFIXES = {
    bankFee: '627',
    feeRebilling: '791',
};
export const REJECTION_ACCOUNT_LABELS = {
    bankFee: 'Services bancaires (627)',
    feeRebilling: 'Transferts de charges (791)',
};
export const rejectPaymentSchema = z
    .object({
    // Date the bank rejected it, and the écriture's date. Deliberately free of the payment's own
    // period: a rejet legitimately lands in a later exercice (§4.2).
    rejectionDate: z.coerce.date(),
    // What the bank charged the CSE. Absent or zero means it charged nothing, and then neither the
    // 627 nor the 791 movement is posted. Entering one always re-invoices it to the salarié — the
    // maquette offers no choice and the ticket says « majoré des frais ».
    bankFeeAmount: zodMoney().optional(),
    // Free text: the accountant qualifies the rejection in the bank's own words.
    reason: z.string().max(500).optional(),
    // Reschedules the debt to a new date. Creates a règlement to come, **no écriture**: the debt
    // is already back on the 411 through the extourne (§3.10). Without it, the dossiers the
    // règlement paid owe the amount again, to be paid with any mode.
    rescheduledDate: z.coerce.date().optional(),
})
    .refine((data) => !data.bankFeeAmount || data.bankFeeAmount.gte(0), {
    message: 'Les frais de rejet ne peuvent pas être négatifs',
    path: ['bankFeeAmount'],
})
    .refine((data) => !data.rescheduledDate || data.rescheduledDate >= data.rejectionDate, {
    message: 'La date de report ne peut pas précéder la date de rejet',
    path: ['rescheduledDate'],
});
// Collecting an expected payment (rescheduled after a rejection): the replacement instrument was
// handed over, so the capture écriture (transit / 411) posts now. The mode can change — a bounced
// cheque is often replaced by cash — and the instrument number is required when the mode carries
// one.
export const collectPaymentSchema = z.object({
    collectionDate: z.coerce.date(),
    financialAccountId: z.number().int().positive().optional(),
    paymentMethodPreset: z.enum(PaymentMethodPreset).nullable().optional(),
    instrumentNumber: z.string().min(1).optional(),
});
